
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  useDeleteApiCompaniesCurrentServicemodulesServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { notify } from '@/lib/notify'
import { PermissionEntityType } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/shadcn/tooltip.tsx";

interface Props {
  id: number
  moduleName: string | null;
  dialogTitle: string
  onClose: () => void
  /** Column 1: standard fields. */
  standard: ReactNode
  /** Column 2: type-specific fields. If missing, the editor is single-column. */
  specific?: ReactNode
  onSave: () => void
  isSaving: boolean
  hasChildren?: boolean
}

/**
 * Shared shell of all module editors: header + two-column body
 * (standard | separator | specifics) + footer. Deletion is identical for all
 * types and therefore anchored centrally here.
 */
export function EditServiceModuleDialog({id, dialogTitle, moduleName, onClose, standard, specific, onSave, isSaving, hasChildren }: Props) {
  const { t } = useTranslation(['service-modules', 'common'])
  const queryClient = useQueryClient()
  const { userHasEntityPermission } = usePermissions()
  const canDelete = userHasEntityPermission(PermissionEntityType.ServiceModules, 'Delete', id)
  const del = useDeleteApiCompaniesCurrentServicemodulesServiceModuleId()
  const [confirmDelete, setConfirmDelete] = useState(false)

  async function handleDelete() {
    try {
      await del.mutateAsync({ serviceModuleId: id })
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
      notify.success(t('toasts.deleted'))
      setConfirmDelete(false)
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.deleteError'))
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {dialogTitle}
        </DialogTitle>
      </DialogHeader>

      {specific ? (
        <div className="grid grid-cols-[1fr_auto_1fr] gap-6 py-2">
          <div className="flex flex-col gap-4 min-w-0">{standard}</div>
          <Separator orientation="vertical" />
          <div className="flex flex-col gap-4 min-w-0">{specific}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 py-2">{standard}</div>
      )}

      <DialogFooter className="flex-row justify-between sm:justify-between">
        {canDelete ? (
            <Tooltip>
              <TooltipTrigger asChild>
            <span>
                <Button
                    variant="destructive"
                    onClick={() => setConfirmDelete(true)}
                    disabled={del.isPending || hasChildren}
                >
                <Trash2 className="size-4 mr-1" />
                {t('buttons.delete', { ns: 'common' })}
                </Button>
            </span>
              </TooltipTrigger>
              {hasChildren && (
                  <TooltipContent>
                    {t('editShell.folderNotEmpty')}
                  </TooltipContent>
              )}
            </Tooltip>
        ) : (
            <span />
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>{t('buttons.cancel', { ns: 'common' })}</Button>
          <Button onClick={onSave} disabled={isSaving}>{t('buttons.save', { ns: 'common' })}</Button>
        </div>
      </DialogFooter>

      <ConfirmDialog
        open={confirmDelete}
        title={t('editShell.confirmDelete.title')}
        description={t('editShell.confirmDelete.description', { name: moduleName ?? t('editShell.moduleFallback') })}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={del.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  )
}
